// Tracks whether the next navigation is a "back" (pop) navigation so the
// router's view-transition `types` function can pick the right CSS animation.
// Set when:
//   - router.history.back() / .forward() / .go(-n) is called
//   - the browser fires popstate (covers iOS swipe-back and hardware back)
// Cleared by `consumeBackNavigation()` on the next navigation.

export const TOP_LEVEL_PATHS = new Set([
  '/home',
  '/conversations',
  '/assistant',
  '/operations',
  '/schedule',
  '/more',
])

export const isTopLevel = (pathname: string) => TOP_LEVEL_PATHS.has(pathname)

let backFlag = false
// Set when a patched history method triggers a popstate we're about to receive.
// Prevents the popstate listener from re-asserting backFlag *after* the router
// has already consumed it during the same navigation, which would pollute the
// flag and reverse the direction of the next forward navigation.
let expectingPopstate = false

export function markBackNavigation() {
  backFlag = true
}

export function consumeBackNavigation(): boolean {
  if (backFlag) {
    backFlag = false
    return true
  }
  return false
}

interface PatchableHistory {
  back: () => void
  forward: () => void
  go?: (delta: number) => void
}

let installed = false

export function installBackNavigationDetection(history: PatchableHistory) {
  if (installed) return
  installed = true

  const originalBack = history.back.bind(history)
  const originalForward = history.forward.bind(history)
  const originalGo = history.go?.bind(history)

  history.back = () => {
    markBackNavigation()
    expectingPopstate = true
    originalBack()
  }
  history.forward = () => {
    markBackNavigation()
    expectingPopstate = true
    originalForward()
  }
  if (originalGo) {
    history.go = (delta: number) => {
      if (delta < 0) {
        markBackNavigation()
        expectingPopstate = true
      }
      originalGo(delta)
    }
  }

  window.addEventListener('popstate', () => {
    if (expectingPopstate) {
      expectingPopstate = false
      return
    }
    markBackNavigation()
  })
}
